/**
 * Service Courses — READ-ONLY
 * Real schema: column is `title` (not `name`), `estimated_duration_minutes` (not `duration_minutes`)
 */
import { supabase } from '../config/supabase';
import { Course } from '../types';

const toCourse = (data: Record<string, unknown>): Course => ({
  id: data.id as string,
  organizationId: data.organization_id as string,
  title: data.title as string,
  description: data.description as string | undefined,
  thumbnailUrl: data.thumbnail_url as string | undefined,
  estimatedDurationMinutes: data.estimated_duration_minutes as number | undefined,
  isPublished: (data.is_published as boolean) ?? true,
  certificateEnabled: (data.certificate_enabled as boolean) ?? false,
  prerequisiteCourseId: data.prerequisite_course_id as string | undefined,
  createdAt: new Date(data.created_at as string),
  updatedAt: new Date((data.updated_at as string) || (data.created_at as string)),
});

export const coursesService = {
  async getByOrganization(_organizationId: string): Promise<Course[]> {
    // RLS handles visibility (owned + shared courses for the user's org)
    const { data, error } = await supabase
      .from('courses')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching courses:', error);
      throw new Error('Impossible de charger les formations');
    }

    return (data || []).map(toCourse);
  },

  async getPublishedByOrganization(_organizationId: string): Promise<Course[]> {
    // RLS handles visibility (owned + shared courses for the user's org)
    const { data, error } = await supabase
      .from('courses')
      .select('*')
      .eq('is_published', true)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching published courses:', error);
      throw new Error('Impossible de charger les formations');
    }

    return (data || []).map(toCourse);
  },

  async getById(id: string): Promise<Course | null> {
    const { data, error } = await supabase
      .from('courses')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) {
      console.error('Error fetching course:', error);
      throw new Error('Impossible de charger la formation');
    }

    return data ? toCourse(data) : null;
  },

  async countByOrganization(_organizationId: string): Promise<number> {
    // RLS handles visibility (owned + shared courses for the user's org)
    const { count, error } = await supabase
      .from('courses')
      .select('*', { count: 'exact', head: true });

    if (error) {
      console.error('Error counting courses:', error);
      return 0;
    }

    return count || 0;
  },

  async getAll(): Promise<Course[]> {
    const { data, error } = await supabase
      .from('courses')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching all courses:', error);
      throw new Error('Impossible de charger les formations');
    }

    return (data || []).map(toCourse);
  },
};
